import React, { useState, useEffect, useCallback } from 'react';
import { isSuperAdmin } from '../utils/permissions';

const API = process.env.REACT_APP_API_URL || '';

export default function HomeChargersPage({ user }) {
  const [chargers, setChargers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const limit = 25;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const canManage = isSuperAdmin(user);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` };

  const fetchChargers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filterStatus) params.append('status', filterStatus);
      if (search) params.append('search', search);

      const res = await fetch(`${API}/api/admin/home-chargers?${params}`, { headers: authHeaders });
      const data = await res.json();
      setChargers(data.chargers || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch home chargers:', err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterStatus, search]);

  useEffect(() => { fetchChargers(); }, [fetchChargers]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await fetch(`${API}/api/admin/home-chargers/${id}`, { headers: authHeaders });
      const data = await res.json();
      setDetail(data);
    } catch (err) {
      console.error('Failed to fetch home charger detail:', err);
    }
    setDetailLoading(false);
  };

  const closeDetail = () => { setSelectedId(null); setDetail(null); };

  const runAction = async (action) => {
    if (!selectedId) return;
    const confirmMsg = action === 'deactivate'
      ? 'Deactivate this home charger? It will disappear from the owner\'s app and may reappear on the public map.'
      : 'Restore this home charger to active status?';
    if (!window.confirm(confirmMsg)) return;

    setActionLoading(true);
    try {
      await fetch(`${API}/api/admin/home-chargers/${selectedId}/${action}`, {
        method: 'PATCH',
        headers: authHeaders,
      });
      await openDetail(selectedId);
      fetchChargers();
    } catch (err) {
      console.error(`Failed to ${action} home charger:`, err);
    }
    setActionLoading(false);
  };

  const statusColors = {
    active: { background: '#F0FDF4', color: '#16A34A' },
    pending: { background: '#FFFBEB', color: '#D97706' },
    declined: { background: '#F3F4F6', color: '#9CA3AF' },
    removed: { background: '#F3F4F6', color: '#9CA3AF' },
    admin_disabled: { background: '#FEF2F2', color: '#DC2626' },
    unclaimed: { background: '#EFF6FF', color: '#2563EB' },
  };

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Home Chargers</h1>
          <p style={{ color: '#6B7280', fontSize: 14, marginTop: 4 }}>{total} home charger{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search serial, owner name, or email..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={inputStyle}
        />
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Statuses</option>
          <option value="unclaimed">Unclaimed (inventory)</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="declined">Declined</option>
          <option value="removed">Removed</option>
          <option value="admin_disabled">Admin Disabled</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              <th style={thStyle}>Owner</th>
              <th style={thStyle}>Serial</th>
              <th style={thStyle}>Nickname</th>
              <th style={thStyle}>Role</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Charger</th>
              <th style={thStyle}>Added</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
            ) : chargers.length === 0 ? (
              <tr><td colSpan="8" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>No home chargers found</td></tr>
            ) : (
              chargers.map(c => (
                <tr
                  key={c.charger_id}
                  style={{ borderTop: '1px solid #F3F4F6', cursor: c.id ? 'pointer' : 'default' }}
                  onClick={() => c.id && openDetail(c.id)}
                >
                  <td style={tdStyle}>
                    {c.id ? (
                      <>{c.first_name} {c.last_name}<div style={{ fontSize: 12, color: '#9CA3AF' }}>{c.email}</div></>
                    ) : (
                      <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not yet claimed</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{c.serial_number}</td>
                  <td style={tdStyle}>{c.nickname || c.charger_name || '—'}</td>
                  <td style={{ ...tdStyle, textTransform: 'capitalize' }}>{c.role || '—'}</td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeBase, ...(statusColors[c.status] || statusColors.removed) }}>{c.status.replace('_', ' ')}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeBase, ...(c.is_online ? { background: '#F0FDF4', color: '#16A34A' } : { background: '#F3F4F6', color: '#9CA3AF' }) }}>
                      {c.is_online ? 'Online' : 'Offline'}
                    </span>
                    {c.power_rating && (
                      <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>{c.power_rating >= 1000 ? c.power_rating / 1000 : c.power_rating}kW · {c.connector_type || '—'}</div>
                    )}
                  </td>
                  <td style={tdStyle}>{c.added_at ? new Date(c.added_at).toLocaleDateString('en-GB') : '—'}</td>
                  <td style={tdStyle}>{c.id && <span style={{ color: '#3B82F6', fontSize: 13, fontWeight: 600 }}>View →</span>}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle}>← Prev</button>
          <span style={{ padding: '8px 16px', fontSize: 14, color: '#6B7280' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle}>Next →</button>
        </div>
      )}

      {/* Detail modal */}
      {selectedId && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000, padding: 24 }} onClick={closeDetail}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 32, maxWidth: 560, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            {detailLoading || !detail ? (
              <p style={{ color: '#9CA3AF', textAlign: 'center', padding: 40 }}>Loading...</p>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, fontFamily: 'monospace' }}>{detail.charger.serial_number}</h2>
                    <p style={{ color: '#6B7280', fontSize: 14, marginTop: 4 }}>{detail.charger.nickname || 'No nickname set'}</p>
                  </div>
                  <span style={{ ...badgeBase, ...(statusColors[detail.charger.status] || statusColors.removed) }}>{detail.charger.status.replace('_', ' ')}</span>
                </div>

                <div style={{ marginBottom: 20, display: 'flex', gap: 20, alignItems: 'center' }}>
                  <span style={{ ...badgeBase, ...(detail.charger.is_online ? { background: '#F0FDF4', color: '#16A34A' } : { background: '#F3F4F6', color: '#9CA3AF' }) }}>
                    {detail.charger.is_online ? 'Online' : 'Offline'}
                  </span>
                  {detail.charger.power_rating && (
                    <span style={{ fontSize: 13, color: '#6B7280' }}>
                      {detail.charger.power_rating >= 1000 ? detail.charger.power_rating / 1000 : detail.charger.power_rating}kW · {detail.charger.connector_type || '—'}
                      {detail.charger.price_per_kwh ? ` · £${parseFloat(detail.charger.price_per_kwh).toFixed(2)}/kWh` : ''}
                    </span>
                  )}
                  {detail.charger.last_heartbeat && (
                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>Last seen {new Date(detail.charger.last_heartbeat).toLocaleString('en-GB')}</span>
                  )}
                </div>

                <div style={{ marginBottom: 20 }}>
                  <h3 style={sectionTitle}>Owner</h3>
                  <p style={{ fontSize: 14, margin: 0 }}>{detail.charger.first_name} {detail.charger.last_name} — <a href={`mailto:${detail.charger.email}`} style={{ color: '#3B82F6' }}>{detail.charger.email}</a></p>
                  <p style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>Added {new Date(detail.charger.added_at).toLocaleDateString('en-GB')}</p>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <h3 style={sectionTitle}>Household ({detail.household.length})</h3>
                  {detail.household.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#9CA3AF' }}>No household members</p>
                  ) : (
                    detail.household.map(m => (
                      <div key={m.id} style={{ fontSize: 13, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                        {m.first_name} {m.last_name} · <span style={{ textTransform: 'capitalize', color: '#6B7280' }}>{m.role}</span> · <span style={{ textTransform: 'capitalize', color: '#6B7280' }}>{m.status}</span>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={sectionTitle}>Recent Sessions ({detail.sessions.length})</h3>
                  {detail.sessions.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#9CA3AF' }}>No charging sessions yet</p>
                  ) : (
                    detail.sessions.map(s => (
                      <div key={s.id} style={{ fontSize: 13, padding: '6px 0', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{new Date(s.start_time).toLocaleDateString('en-GB')}</span>
                        <span>{s.energy_consumed != null ? `${parseFloat(s.energy_consumed).toFixed(1)} kWh` : '—'}</span>
                        <span style={{ color: '#6B7280', textTransform: 'capitalize' }}>{s.status}</span>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button onClick={closeDetail} style={pageBtnStyle}>Close</button>
                  {canManage && detail.charger.status === 'admin_disabled' && (
                    <button onClick={() => runAction('restore')} disabled={actionLoading} style={{ ...pageBtnStyle, background: '#10B981', color: 'white', border: 'none' }}>
                      {actionLoading ? 'Restoring...' : 'Restore'}
                    </button>
                  )}
                  {canManage && detail.charger.status !== 'admin_disabled' && (
                    <button onClick={() => runAction('deactivate')} disabled={actionLoading} style={{ ...pageBtnStyle, background: '#DC2626', color: 'white', border: 'none' }}>
                      {actionLoading ? 'Deactivating...' : 'Deactivate'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = { padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, minWidth: 220, background: '#F9FAFB' };
const selectStyle = { padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, background: '#F9FAFB', cursor: 'pointer' };
const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '14px 16px', fontSize: 14 };
const badgeBase = { padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' };
const pageBtnStyle = { padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 };
const sectionTitle = { fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 };
